import { useRef, useState, useCallback, useMemo } from 'react';
import { number, string, func, shape, bool } from 'prop-types';
import delve from 'dlv';
import { line, curveMonotoneX } from 'd3-shape';

import useResizeObserver from 'hooks/useResizeObserver';
import TrendsPropType from 'propTypes/trends';

import { AnimatedLoadingWrapperGroup } from 'components/base/AnimatedLoadingWrapper/index';
import { MarginGroup } from 'components/data-visualization/BubbleChart/MarginGroup';
import { Figure, FigCaption } from './shared.style';
import Axes from './Axes';
import TrendLines from './TrendLines';
import LineCaps from './LineCaps';
import SkeletonLoader from './SkeletonLoader';
import Layout from 'components/styled/Layout/Layout';
import Placeholder from 'components/data-visualization/Table/Placeholder';
import { isLineChartPathDefined } from './utils/pathDefinitions';

export default LineChart;

/**
 * A non-interactive linechart, to be used initially for components on the
 * Overview topic.
 * Chart dibs are drawn as a dash line with a single line cap
 *
 * @param {object} props React props
 * @param {string} props.caption The caption to show above the graph
 * @param {number} [props.chartHeight=150] The height of the graph
 * @param {function} props.colorScale Ordinal scale used to get trend line colors
 * @param {boolean} props.isFirstLoad Is this the first time data is being fetched?
 * @param {boolean} props.isPending Is data being fetched?
 * @param {object} [props.margin={ top: 2, right: 2, bottom: 24, left: 2 }] Graph margins, following d3.js margin conventions
 * @param {array} props.trends The trend data to display in the graph
 * @param {function} props.xScale The scale for the horizontal (x) axis
 * @param {function} props.yScale The scale for the vertical (y) axis
 */
function LineChart(props) {
  const {
    caption,
    chartHeight = 150,
    colorScale,
    isFirstLoad,
    isPending,
    margin = { top: 2, right: 2, bottom: 24, left: 2 },
    trends,
    xScale,
    yScale,
  } = props;

  /**
   * Local state variables
   */
  const [chartWidth, setChartWidth] = useState(350);

  /**
   * Make sure the chart can resize on the x-axis
   */
  const chartContainer = useRef();

  useResizeObserver(
    chartContainer,
    useCallback((entries) => {
      const el = entries[0];
      const width = delve(el, ['contentRect', 'width']);
      setChartWidth(Math.round(width));
    }, [])
  );

  /**
   * Calculate the width of the drawing area
   */
  const width = useMemo(
    () => chartWidth - margin.left - margin.right,
    [chartWidth, margin]
  );

  /**
   * Calculate the height of the drawing area
   */
  const height = useMemo(
    () => chartHeight - margin.top - margin.bottom,
    [chartHeight, margin]
  );

  /**
   * Clone the scales and update their range with the width/height of the
   * drawing areas.
   */
  const x = useCallback(xScale.copy().range([0, width]), [xScale, width]);
  const y = useCallback(yScale.copy().range([height, 0]), [yScale, height]);

  /**
   * Create a SVG path renderer for the trend lines.
   */
  const path = useCallback(
    line()
      .x((d) => x(d.periodKey))
      .y((d) => y(d.value))
      .defined((d) => isLineChartPathDefined(d))
      .curve(curveMonotoneX),
    [x, y]
  );

  /**
   * True if there are no trends to display.
   * @returns {boolean}
   */
  const hasNoData = useMemo(() => {
    if (!trends) return false;
    if (trends.length === 0) return true;
    return trends.every((trend) => trend.data.every((d) => d.value === null));
  }, [trends]);

  return (
    <Figure data-pw="line-chart">
      <FigCaption>{caption}</FigCaption>
      <div ref={chartContainer}>
        {isFirstLoad && (
          <SkeletonLoader
            parentWidth={chartWidth}
            parentHeight={chartHeight}
            width={width}
            height={height}
            margin={margin}
          />
        )}
        {!isFirstLoad && hasNoData && (
          <Layout.Relative>
            <Placeholder type="data" isSmallScreen={false} />
          </Layout.Relative>
        )}
        {!isFirstLoad && !hasNoData && (
          <svg
            style={{ overflow: 'visible', maxWidth: '100%' }}
            height={chartHeight}
            width={chartWidth}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          >
            <AnimatedLoadingWrapperGroup loading={isPending}>
              <Axes x={x} y={y} height={height} margin={margin} />
              <MarginGroup margin={margin}>
                <TrendLines
                  colorScale={colorScale}
                  hasDashedGaps
                  hasInterpolatedTrend
                  height={height}
                  path={path}
                  trends={trends}
                  width={width}
                  x={x}
                />
                <LineCaps trends={trends} colorScale={colorScale} x={x} y={y} />
              </MarginGroup>
            </AnimatedLoadingWrapperGroup>
          </svg>
        )}
      </div>
    </Figure>
  );
}

LineChart.propTypes = {
  caption: string.isRequired,
  chartHeight: number,
  colorScale: func.isRequired,
  isFirstLoad: bool.isRequired,
  isPending: bool.isRequired,
  margin: shape({
    top: number.isRequired,
    right: number.isRequired,
    bottom: number.isRequired,
    left: number.isRequired,
  }),
  trends: TrendsPropType.isRequired,
  xScale: func.isRequired,
  yScale: func.isRequired,
};
